import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'
import moment from 'moment'

// The activity feed and leaderboards show countries; register English names once so a stored ISO-2
// code can be expanded to a readable label. Guarded because other modules register the same locale.
if (!countries.getNames('en')?.AD) {
  countries.registerLocale(enLocale)
}

// Compact currency for the big TV numbers: kr 1.2M, €840K, $12.3B. Full precision would never fit a
// hero tile and isn't the point — the order of magnitude is what motivates. The display currency comes
// from the snapshot (the API converts the figures server-side), so formatters are built per currency
// and cached rather than hard-coded to euros.
const compactByCurrency = new Map<string, Intl.NumberFormat>()
const fullByCurrency = new Map<string, Intl.NumberFormat>()

function moneyFormatter(
  cache: Map<string, Intl.NumberFormat>,
  currency: string,
  options: Intl.NumberFormatOptions
): Intl.NumberFormat {
  let formatter = cache.get(currency)
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency, ...options })
    cache.set(currency, formatter)
  }
  return formatter
}

const compactCount = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function formatMoneyCompact(value: number, currency: string): string {
  // Compact notation only kicks in above 10k; below that the abbreviation looks odd (€9.9K), so show
  // the rounded full amount for small numbers.
  return Math.abs(value) >= 10_000
    ? moneyFormatter(compactByCurrency, currency, {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value)
    : moneyFormatter(fullByCurrency, currency, { maximumFractionDigits: 0 }).format(value)
}

export function formatCountCompact(value: number): string {
  return Math.abs(value) >= 10_000
    ? compactCount.format(value)
    : new Intl.NumberFormat('en-US').format(Math.round(value))
}

export function formatMetric(
  value: number,
  unit: 'count' | 'money',
  currency: string
): string {
  return unit === 'money' ? formatMoneyCompact(value, currency) : formatCountCompact(value)
}

// Bare magnitude for the hero tiles. The currency is shown in the tile's label (e.g. "Volume traded
// (DKK)"), so the number itself stays clean — "109.1M", not "DKK 109.1M". Count and money render the
// same way; only the label differs.
export function formatMetricValue(value: number): string {
  return formatCountCompact(value)
}

// Week-over-week (or as-of-a-week-ago) percentage move for the trend arrow. Null when there's nothing
// meaningful to compare against (no previous value, or a previous value of zero with no current value).
export function percentChange(
  value: number,
  previous?: number | null
): number | null {
  if (previous == null) return null
  if (previous === 0) return value > 0 ? 100 : null
  return ((value - previous) / Math.abs(previous)) * 100
}

export function relativeTime(utc: string): string {
  return moment.utc(utc).fromNow()
}

// Country code → flag emoji via regional-indicator letters. Accepts ISO-2 (DK) or ISO-3 (DNK) — the
// platform stores alpha-3 — converting alpha-3 down to alpha-2 first. Returns null for anything that
// isn't a resolvable code (e.g. "Unknown", a full name) so the caller can fall back to a globe.
export function countryFlagEmoji(country?: string | null): string | null {
  const alpha2 = countryAlpha2(country)
  if (!alpha2) return null
  const A = 0x1f1e6
  return String.fromCodePoint(
    A + (alpha2.charCodeAt(0) - 65),
    A + (alpha2.charCodeAt(1) - 65)
  )
}

// A readable country label: expand a known ISO-2/ISO-3 code to its English name, otherwise show the
// raw value (already a name, or "Unknown").
export function countryLabel(country?: string | null): string {
  if (!country) return 'Unknown'
  if (/^[a-zA-Z]{2,3}$/.test(country)) {
    return countries.getName(country.toUpperCase(), 'en') ?? country.toUpperCase()
  }
  return country
}

// Normalise a stored country code to an uppercase ISO-2 code, or null if it isn't a code we recognise.
// Exported so the SVG flag component can pick the right asset (the wall board's Chromium renders no
// emoji flags, so we draw real SVGs keyed on this code).
export function countryAlpha2(country?: string | null): string | null {
  if (!country) return null
  const code = country.toUpperCase()
  if (/^[A-Z]{2}$/.test(code)) return code
  if (/^[A-Z]{3}$/.test(code)) {
    const alpha2 = countries.alpha3ToAlpha2(code)
    return alpha2 && /^[A-Z]{2}$/.test(alpha2) ? alpha2 : null
  }
  return null
}
